import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../lib/api/client';
import type { ServerOfferCarts } from './useOfferCartStore';

export const PENDING_LOCATION_ID = '__pending__';
export const PENDING_LOCATION_NAME = 'Pending Location';

export const PICKUP_LOCATION_ID = '__pickup__';
export const PICKUP_LOCATION_NAME = 'Pick Up';

const LOCAL_ONLY_LOCATIONS = new Set([PENDING_LOCATION_ID, PICKUP_LOCATION_ID]);
const API_NONE_LOCATION = '_none';

function toApiLocationId(locationId: string): string {
  return LOCAL_ONLY_LOCATIONS.has(locationId) ? API_NONE_LOCATION : locationId;
}

export interface CartItem {
  listingId: string;
  // Global items carry `productVariantId`; org items carry
  // `orgProductVariantId` (mutually exclusive). Cart ops key by `listingId`,
  // so the variant id is informational — `source` discriminates the catalog.
  source?: 'global' | 'org';
  productVariantId: string;
  orgProductVariantId?: string | null;
  quantity: number;
  unitPrice: number;
  maxQuantity?: number;
  productName: string;
  size: string;
  sku: string;
  brandName?: string;
  imageUrl?: string | null;
  /**
   * Full ordered product image gallery captured at add-time. Optional because
   * server-hydrated carts only persist the legacy single `imageUrl`; the cart
   * UI gracefully degrades to a single image when this is missing.
   */
  imageUrls?: string[];
  color?: string;
  conditionGrade?: string;
  conditionTags?: { name: string; type: string }[];
}

export const isCartItemSoldOut = (item: Pick<CartItem, 'maxQuantity'>) => item.maxQuantity === 0;

const isCartItemCountable = (item: CartItem) => item.quantity > 0 && !isCartItemSoldOut(item);

export interface LocationCart {
  locationId: string;
  locationName: string;
  items: CartItem[];
}

interface ServerLocationCart {
  locationId: string;
  locationName: string;
  items: Array<{
    listingId: string;
    /**
     * Discriminator added in Plan 2C-A/B: `global` items carry productVariantId,
     * `org` items carry orgProductVariantId. The fields are mutually exclusive
     * but typed as `string | null` to mirror the API payload directly.
     */
    source?: 'global' | 'org';
    productVariantId: string | null;
    orgProductVariantId?: string | null;
    quantity: number;
    unitPrice: number;
    maxQuantity: number | null;
    productName: string;
    size: string;
    sku: string;
    brandName: string | null;
    imageUrl: string | null;
    productConditions: string[];
    packagingConditions: string[];
  }>;
}

interface CartStore {
  storeId: string | null;
  selectedLocationId: string | null;
  locationCarts: Record<string, LocationCart>;
  items: CartItem[];
  inventoryReservations: Record<string, number>;
  addItem: (item: CartItem, locationId?: string) => Promise<boolean>;
  removeItem: (listingId: string, locationId?: string) => Promise<void>;
  updateQuantity: (
    listingId: string,
    quantity: number,
    locationId?: string,
    maxQuantity?: number,
  ) => Promise<{ success: boolean; capped?: boolean }>;
  clearCart: (locationId?: string) => Promise<void>;
  resetCart: () => void;
  setStoreId: (id: string) => void;
  setSelectedLocation: (locationId: string | null) => void;
  addLocationCart: (locationId: string, locationName: string) => void;
  removeLocationCart: (locationId: string) => void;
  getTotalAmount: (locationId?: string) => number;
  getItemCount: (locationId?: string) => number;
  getItemQuantity: (listingId: string, locationId?: string) => number;
  getCurrentCartItems: () => CartItem[];
  getAllLocationCarts: () => LocationCart[];
  getReservedQuantity: (listingId: string) => number;
  updateReservation: (listingId: string, delta: number) => void;
  canReserveQuantity: (listingId: string, requestedQty: number, maxAvailable: number) => boolean;
  syncReservationsFromCarts: () => void;
  syncFromServer: (
    storeId: string,
    onOfferCarts?: (offerCarts: ServerOfferCarts) => void,
  ) => Promise<void>;
  migrateLocationCart: (
    fromLocationId: string,
    toLocationId: string,
    toLocationName: string,
  ) => void;
}

function toLocationCart(server: ServerLocationCart): LocationCart {
  // Render BOTH global and org-source items. Display fields (name/size/sku/
  // conditions/image) come from the polymorphic API for either source; cart
  // ops key by `listingId`, so the (possibly null) variant id is informational.
  const items = server.items.map((i) => ({
    listingId: i.listingId,
    source: i.source,
    productVariantId: i.productVariantId ?? '',
    orgProductVariantId: i.orgProductVariantId ?? null,
    quantity: i.quantity,
    unitPrice: i.unitPrice,
    maxQuantity: i.maxQuantity ?? undefined,
    productName: i.productName,
    size: i.size,
    sku: i.sku,
    brandName: i.brandName ?? undefined,
    imageUrl: i.imageUrl,
    conditionGrade: i.productConditions[0],
    conditionTags: i.packagingConditions.map((name) => ({ name, type: 'packaging' })),
  }));
  return {
    locationId: server.locationId,
    locationName: server.locationName,
    items,
  };
}

function computeReservations(carts: Record<string, LocationCart>) {
  const reservations: Record<string, number> = {};
  for (const cart of Object.values(carts)) {
    for (const item of cart.items) {
      reservations[item.listingId] = (reservations[item.listingId] ?? 0) + item.quantity;
    }
  }
  return reservations;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => {
      const syncFromServer = async (
        storeId: string,
        onOfferCarts?: (offerCarts: ServerOfferCarts) => void,
      ) => {
        // Preserve last-known-good local state on failure. A transient network blip
        // (or a malformed payload) must not wipe a shopper's cart, since this method
        // is now called on a 30s + window-focus polling cadence from CartDrawer.
        try {
          const res = await apiClient['wholesale-cart'].store[':storeId'].$get({
            param: { storeId },
          });
          if (!res.ok) return undefined;
          const payload = await res.json();
          if (!('data' in payload)) return undefined;
          const serverCart = payload.data;

          set((state) => {
            const nextLocationCarts: Record<string, LocationCart> = {};

            // Apply server location carts — map __none__ back to pending
            for (const [locId, serverLoc] of Object.entries(serverCart.locationCarts)) {
              const clientLocId = locId === '__none__' ? PENDING_LOCATION_ID : locId;
              const mapped = toLocationCart(serverLoc);
              mapped.locationId = clientLocId;
              if (clientLocId === PENDING_LOCATION_ID) mapped.locationName = PENDING_LOCATION_NAME;
              nextLocationCarts[clientLocId] = mapped;
            }

            return {
              storeId: serverCart.storeId,
              selectedLocationId: serverCart.selectedLocationId ?? state.selectedLocationId,
              locationCarts: nextLocationCarts,
              items: [],
              inventoryReservations: computeReservations(nextLocationCarts),
            };
          });

          if (onOfferCarts && serverCart.offerCarts) {
            onOfferCarts(serverCart.offerCarts);
          }
        } catch {
          // Swallow — preserve last-known-good state. React Query handles retry/backoff.
        }
      };

      const applyServerLocationCarts = (serverCarts: Record<string, ServerLocationCart>) => {
        set(() => {
          const nextLocationCarts: Record<string, LocationCart> = {};

          for (const [locId, serverLoc] of Object.entries(serverCarts)) {
            const clientLocId = locId === '__none__' ? PENDING_LOCATION_ID : locId;
            const mapped = toLocationCart(serverLoc);
            mapped.locationId = clientLocId;
            if (clientLocId === PENDING_LOCATION_ID) mapped.locationName = PENDING_LOCATION_NAME;
            nextLocationCarts[clientLocId] = mapped;
          }

          return {
            locationCarts: nextLocationCarts,
            items: [],
            inventoryReservations: computeReservations(nextLocationCarts),
          };
        });
      };

      return {
        storeId: null,
        selectedLocationId: null,
        locationCarts: {},
        items: [],
        inventoryReservations: {},

        syncFromServer,

        getReservedQuantity: (listingId) => {
          return get().inventoryReservations[listingId] || 0;
        },

        updateReservation: (listingId, delta) => {
          set((state) => {
            const current = state.inventoryReservations[listingId] || 0;
            const next = Math.max(0, current + delta);
            const newReservations = { ...state.inventoryReservations };
            if (next === 0) {
              delete newReservations[listingId];
            } else {
              newReservations[listingId] = next;
            }
            return { inventoryReservations: newReservations };
          });
        },

        canReserveQuantity: (listingId, requestedQty, maxAvailable) => {
          const current = get().inventoryReservations[listingId] || 0;
          return current + requestedQty <= maxAvailable;
        },

        syncReservationsFromCarts: () => {
          set((state) => ({
            inventoryReservations: computeReservations(state.locationCarts),
          }));
        },

        addItem: async (item, locationId) => {
          if (!locationId) return false;

          const state = get();
          const storeId = state.storeId;
          if (!storeId) return false;

          if (item.maxQuantity !== undefined) {
            if (!state.canReserveQuantity(item.listingId, item.quantity, item.maxQuantity)) {
              return false;
            }
          }

          // Optimistic local update
          set((currentState) => {
            const locationCart = currentState.locationCarts[locationId];
            if (!locationCart) return currentState;

            const existingIdx = locationCart.items.findIndex((i) => i.listingId === item.listingId);
            const updatedItems = [...locationCart.items];
            let reservationDelta = item.quantity;

            if (existingIdx > -1) {
              const existing = updatedItems[existingIdx];
              if (existing) {
                existing.quantity = existing.quantity + item.quantity;
                reservationDelta = item.quantity;
              }
            } else {
              updatedItems.push(item);
            }

            const newLocationCarts = { ...currentState.locationCarts };
            newLocationCarts[locationId] = { ...locationCart, items: updatedItems };

            const currentRes = currentState.inventoryReservations[item.listingId] || 0;
            const newReservations = { ...currentState.inventoryReservations };
            newReservations[item.listingId] = currentRes + reservationDelta;

            return { locationCarts: newLocationCarts, inventoryReservations: newReservations };
          });

          try {
            const apiLocId = toApiLocationId(locationId);
            const res = await apiClient['wholesale-cart'].store[':storeId'].locations[
              ':locationId'
            ].items[':listingId'].$post({
              param: { storeId, locationId: apiLocId, listingId: item.listingId },
              json: { delta: item.quantity },
            });
            if (!res.ok) {
              await syncFromServer(storeId);
              return false;
            }
            const payload = await res.json();
            if ('data' in payload) {
              applyServerLocationCarts(payload.data.locationCarts);
            }
            return true;
          } catch {
            await syncFromServer(storeId);
            return false;
          }
        },

        removeItem: async (listingId, locationId) => {
          if (!locationId) return;
          const storeId = get().storeId;

          // Optimistic local update
          set((state) => {
            const locationCart = state.locationCarts[locationId];
            if (!locationCart) return state;

            const itemToRemove = locationCart.items.find((i) => i.listingId === listingId);
            const qtyToRelease = itemToRemove?.quantity || 0;

            const newLocationCarts = { ...state.locationCarts };
            newLocationCarts[locationId] = {
              ...locationCart,
              items: locationCart.items.filter((i) => i.listingId !== listingId),
            };

            const currentRes = state.inventoryReservations[listingId] || 0;
            const newRes = Math.max(0, currentRes - qtyToRelease);
            const newReservations = { ...state.inventoryReservations };
            if (newRes === 0) delete newReservations[listingId];
            else newReservations[listingId] = newRes;

            return { locationCarts: newLocationCarts, inventoryReservations: newReservations };
          });

          if (!storeId) return;

          try {
            const apiLocId = toApiLocationId(locationId);
            const res = await apiClient['wholesale-cart'].store[':storeId'].locations[
              ':locationId'
            ].items[':listingId'].$delete({
              param: { storeId, locationId: apiLocId, listingId },
            });
            if (!res.ok) {
              await syncFromServer(storeId);
              return;
            }
            const payload = await res.json();
            if ('data' in payload) {
              applyServerLocationCarts(payload.data.locationCarts);
            }
          } catch {
            if (storeId) await syncFromServer(storeId);
          }
        },

        updateQuantity: async (listingId, quantity, locationId, maxQuantity) => {
          if (!locationId) return { success: false };
          if (quantity < 0) return { success: false };

          const state = get();
          const storeId = state.storeId;
          let currentQuantity = 0;
          let finalQuantity = quantity;
          let capped = false;

          const locationCart = state.locationCarts[locationId];
          if (!locationCart) return { success: false };
          const existingItem = locationCart.items.find((i) => i.listingId === listingId);
          currentQuantity = existingItem?.quantity || 0;

          const delta = quantity - currentQuantity;

          if (delta > 0 && maxQuantity !== undefined) {
            const currentRes = state.inventoryReservations[listingId] || 0;
            const resInOther = currentRes - currentQuantity;
            const newTotal = resInOther + quantity;
            if (newTotal > maxQuantity) {
              finalQuantity = Math.max(0, maxQuantity - resInOther);
              capped = true;
              if (finalQuantity <= 0) return { success: false };
            }
          }

          // Optimistic local update
          set((currentState) => {
            const lc = currentState.locationCarts[locationId];
            if (!lc) return currentState;

            const newLocationCarts = { ...currentState.locationCarts };
            const updatedItems =
              finalQuantity === 0
                ? lc.items.filter((item) => item.listingId !== listingId)
                : lc.items.map((item) =>
                    item.listingId === listingId ? { ...item, quantity: finalQuantity } : item,
                  );
            newLocationCarts[locationId] = { ...lc, items: updatedItems };

            const resDelta = finalQuantity - currentQuantity;
            const curRes = currentState.inventoryReservations[listingId] || 0;
            const newRes = Math.max(0, curRes + resDelta);
            const newReservations = { ...currentState.inventoryReservations };
            if (newRes === 0) delete newReservations[listingId];
            else newReservations[listingId] = newRes;

            return { locationCarts: newLocationCarts, inventoryReservations: newReservations };
          });

          if (!storeId) return { success: true, capped };

          try {
            const apiLocId = toApiLocationId(locationId);
            const res = await apiClient['wholesale-cart'].store[':storeId'].locations[
              ':locationId'
            ].items[':listingId'].$patch({
              param: { storeId, locationId: apiLocId, listingId },
              json: { quantity: finalQuantity },
            });
            if (!res.ok) {
              await syncFromServer(storeId);
              return { success: false };
            }
            const payload = await res.json();
            if ('data' in payload) {
              const serverData = payload.data;
              if ('cart' in serverData) {
                applyServerLocationCarts(serverData.cart.locationCarts);
                return { success: true, capped: serverData.capped || capped };
              }
            }
            return { success: true, capped };
          } catch {
            if (storeId) await syncFromServer(storeId);
            return { success: false };
          }
        },

        clearCart: async (locationId) => {
          const storeId = get().storeId;

          set((state) => {
            if (locationId) {
              const locationCart = state.locationCarts[locationId];
              if (!locationCart) return state;

              const newReservations = { ...state.inventoryReservations };
              for (const item of locationCart.items) {
                const cur = newReservations[item.listingId] || 0;
                const next = Math.max(0, cur - item.quantity);
                if (next === 0) delete newReservations[item.listingId];
                else newReservations[item.listingId] = next;
              }

              const newLocationCarts = { ...state.locationCarts };
              if (newLocationCarts[locationId]) {
                newLocationCarts[locationId] = { ...newLocationCarts[locationId], items: [] };
              }
              return { locationCarts: newLocationCarts, inventoryReservations: newReservations };
            }

            return { items: [], storeId: null, inventoryReservations: {} };
          });

          if (!storeId || !locationId) return;

          try {
            const apiLocId = toApiLocationId(locationId);
            const res = await apiClient['wholesale-cart'].store[':storeId'].locations[
              ':locationId'
            ].items.$delete({
              param: { storeId, locationId: apiLocId },
            });
            if (!res.ok) {
              await syncFromServer(storeId);
              return;
            }
            const payload = await res.json();
            if ('data' in payload) {
              applyServerLocationCarts(payload.data.locationCarts);
            }
          } catch {
            if (storeId) await syncFromServer(storeId);
          }
        },

        resetCart: () => {
          set({
            storeId: null,
            selectedLocationId: null,
            locationCarts: {},
            items: [],
            inventoryReservations: {},
          });
        },

        setStoreId: (id) => {
          set({ storeId: id });
        },

        setSelectedLocation: (locationId) => {
          set({ selectedLocationId: locationId });
        },

        addLocationCart: (locationId, locationName) => {
          set((state) => {
            if (state.locationCarts[locationId]) return state;
            return {
              locationCarts: {
                ...state.locationCarts,
                [locationId]: { locationId, locationName, items: [] },
              },
            };
          });
        },

        removeLocationCart: (locationId) => {
          set((state) => {
            const locationCart = state.locationCarts[locationId];
            if (!locationCart) return state;

            const newReservations = { ...state.inventoryReservations };
            for (const item of locationCart.items) {
              const cur = newReservations[item.listingId] || 0;
              const next = Math.max(0, cur - item.quantity);
              if (next === 0) delete newReservations[item.listingId];
              else newReservations[item.listingId] = next;
            }

            const newLocationCarts = { ...state.locationCarts };
            delete newLocationCarts[locationId];
            return {
              locationCarts: newLocationCarts,
              selectedLocationId:
                state.selectedLocationId === locationId ? null : state.selectedLocationId,
              inventoryReservations: newReservations,
            };
          });
        },

        getTotalAmount: (locationId) => {
          const state = get();
          if (locationId) {
            const lc = state.locationCarts[locationId];
            if (!lc) return 0;
            return lc.items
              .filter(isCartItemCountable)
              .reduce((t, i) => t + i.unitPrice * i.quantity, 0);
          }
          return state.items
            .filter(isCartItemCountable)
            .reduce((t, i) => t + i.unitPrice * i.quantity, 0);
        },

        getItemCount: (locationId) => {
          const state = get();
          if (locationId) {
            const lc = state.locationCarts[locationId];
            if (!lc) return 0;
            return lc.items.filter(isCartItemCountable).reduce((c, i) => c + i.quantity, 0);
          }
          return state.items.filter(isCartItemCountable).reduce((c, i) => c + i.quantity, 0);
        },

        getItemQuantity: (listingId, locationId) => {
          const state = get();
          if (locationId) {
            const lc = state.locationCarts[locationId];
            if (!lc) return 0;
            return lc.items.find((i) => i.listingId === listingId)?.quantity || 0;
          }
          return state.items.find((i) => i.listingId === listingId)?.quantity || 0;
        },

        getCurrentCartItems: () => {
          const state = get();
          if (state.selectedLocationId) {
            return state.locationCarts[state.selectedLocationId]?.items || [];
          }
          return state.items;
        },

        getAllLocationCarts: () => Object.values(get().locationCarts),

        migrateLocationCart: (fromLocationId, toLocationId, toLocationName) => {
          // Optimistic local update
          set((state) => {
            const fromCart = state.locationCarts[fromLocationId];
            if (!fromCart || fromCart.items.length === 0) return state;

            const existingToCart = state.locationCarts[toLocationId];
            const toItems = existingToCart ? [...existingToCart.items] : [];

            for (const item of fromCart.items) {
              const existingIdx = toItems.findIndex((i) => i.listingId === item.listingId);
              if (existingIdx > -1) {
                const existing = toItems[existingIdx];
                if (existing) existing.quantity += item.quantity;
              } else {
                toItems.push({ ...item });
              }
            }

            const newLocationCarts = { ...state.locationCarts };
            newLocationCarts[toLocationId] = {
              locationId: toLocationId,
              locationName: toLocationName,
              items: toItems,
            };
            delete newLocationCarts[fromLocationId];

            return {
              locationCarts: newLocationCarts,
              selectedLocationId: toLocationId,
              inventoryReservations: computeReservations(newLocationCarts),
            };
          });

          // Persist migration to server
          const storeId = get().storeId;
          if (!storeId) return;

          const apiFromLocId = toApiLocationId(fromLocationId);
          const apiToLocId = toApiLocationId(toLocationId);
          apiClient['wholesale-cart'].store[':storeId']['migrate-location']
            .$post({
              param: { storeId },
              json: { fromLocationId: apiFromLocId, toLocationId: apiToLocId },
            })
            .then(async (res) => {
              if (!res.ok) {
                await syncFromServer(storeId);
                return;
              }
              const payload = await res.json();
              if ('data' in payload) {
                applyServerLocationCarts(payload.data.locationCarts);
              }
            })
            .catch(() => {
              syncFromServer(storeId);
            });
        },
      };
    },
    {
      name: 'wholesale-cart-storage',
      partialize: (state) => ({
        storeId: state.storeId,
        selectedLocationId: state.selectedLocationId,
        locationCarts: state.locationCarts,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.syncReservationsFromCarts();
        }
      },
    },
  ),
);
